import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileText,
  Download,
  Eye,
  Calendar,
  IndianRupee,
  TrendingUp,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  getEmployeePayslips,
} from "@/services/payroll.service";
import { getSettings } from "@/services/adminService";
// Payslip amounts can carry paise (LOP proration), so show 2 decimals here —
// the app-wide formatCurrency rounds to whole rupees.
const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
import {useDebounce} from "@/utils/useDebounce";
import { isAuthExpiryError } from "@/utils/errorHandler";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Skeleton } from "@/components/ui/skeleton";

// Helper function to convert month number to name
const getMonthName = (monthNumber) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[monthNumber - 1] || monthNumber;
};

// Helper function to get days in month
const getDaysInMonth = (month, year) => {
  return new Date(year, month, 0).getDate();
};

const getTotalLeaves = (payslip = {}) => {
  const explicitTotal = Number(
    payslip.total_leaves ??
      payslip.total_leave ??
      payslip.leave_days ??
      payslip.total_leave_days,
  );

  if (!Number.isNaN(explicitTotal) && explicitTotal > 0) {
    return explicitTotal;
  }

  const paidLeaves = Number(
    payslip.total_paid_leaves ?? payslip.paid_leaves ?? payslip.paid_leave_days,
  );
  const unpaidLeaves = Number(
    payslip.total_unpaid_leaves ??
      payslip.unpaid_leaves ??
      payslip.lop_days ??
      payslip.unpaid_leave_days,
  );

  const combinedLeaves =
    (Number.isNaN(paidLeaves) ? 0 : paidLeaves) +
    (Number.isNaN(unpaidLeaves) ? 0 : unpaidLeaves);

  return combinedLeaves > 0 ? combinedLeaves : 0;
};

// Unpaid leave (LOP) is applied by the backend as a *reduction of earnings*, not as a
// deduction line: `earnings`/`gross_salary` arrive already prorated, while `monthly_earnings`/
// `monthly_gross_pay` carry the full-month figures. The payslip shows the full monthly salary
// under Earnings and surfaces the docked amount as an explicit "Loss of Pay" deduction, so
// Net Salary is unchanged but the employee can see why they were paid less.
const getPayslipBreakdown = (payslip = {}) => {
  const monthlyEarnings = payslip.monthly_earnings?.length
    ? payslip.monthly_earnings
    : payslip.earnings || [];

  const monthlyGross = Number(
    payslip.monthly_gross_pay ?? payslip.gross_salary ?? 0,
  );
  const paidGross = Number(payslip.gross_salary ?? 0);

  // Keep paise — rounding here dropped the decimals from Total Deductions / Net.
  const lopAmount = Math.max(+(monthlyGross - paidGross).toFixed(2), 0);
  const lopDays = Number(payslip.total_unpaid_leaves ?? payslip.lop_days ?? 0) || 0;

  return {
    earnings: monthlyEarnings,
    grossPay: monthlyGross,
    lopAmount,
    lopLabel: lopDays
      ? `Loss of Pay (${lopDays} ${lopDays === 1 ? "day" : "days"})`
      : "Loss of Pay",
    totalDeductions: Number(payslip.total_deductions || 0) + lopAmount,
  };
};

// `deductions.loan_emi` and the top-level `loan_deductions` are the same figure; the
// dedicated "Loan EMI" row renders it, so skip it here to avoid a duplicate line.
const isDuplicateLoanKey = (key) =>
  key === "loan_emi" || key === "loan_deductions";

const EmployeePayslips = (employeeId) => {
  const { user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [previewPayslip, setPreviewPayslip] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  // Whole-history totals for the three cards above the list. Served by the API
  // alongside the page so the cards never depend on which page is open, and so
  // the browser never has to pull every payslip just to add them up.
  const [summary, setSummary] = useState(null);
  const debouncedSearch = useDebounce(searchTerm, 500);
  const debouncedYear = useDebounce(yearFilter, 300);
  const debouncedType = useDebounce(typeFilter, 300);
  const [open, setOpen] = useState(false);

  // Full-month earnings + explicit Loss of Pay line for the preview dialog.
  const previewBreakdown = previewPayslip
    ? getPayslipBreakdown(previewPayslip)
    : null;

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    setPage(1); // reset page when filters change
  }, [debouncedSearch, debouncedYear, debouncedType]);

  useEffect(() => {
    fetchPayslips();
  }, [page, debouncedSearch, debouncedYear, debouncedType]);
 
  const fetchCompanySettings = async () => {
    try {
      const response = await getSettings();
      setCompanySettings(response.data.company_settings);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching company settings:", error);
      }
    }
  };

  const formatLabel = (str) => {
    if (!str) return "";

    // Known payroll acronyms → always uppercase
    const ACRONYMS = [
      "epf",
      "esi",
      "pf",
      "tds",
      "lwf",
      "ctc",
      "hra",
      "da",
      "pt",
      "esi",
      "uans",
    ];

    return str
      .replace(/_/g, " ") // replace underscores with spaces
      .split(" ")
      .map((word) => {
        const lower = word.toLowerCase();
        // If word is a known acronym → return uppercased
        if (ACRONYMS.includes(lower)) return lower.toUpperCase();

        // Otherwise → capitalize normally
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(" ");
  };

  const fetchPayslips = async () => {
    try {
      // if (page === 1 && !payslips.length) {
      //   setInitialLoading(true);
      // } else {
        setListLoading(true);
      // }

      const response = await getEmployeePayslips({
        employeeId,
        page,
        limit: 6,
        search: debouncedSearch,
        year: debouncedYear,
        type: debouncedType,
      });

      const payslipsData = response.payslips;
      // Set before the early return below: an empty page still carries the
      // employee's overall totals.
      setSummary(response.summary || null);
      if (payslipsData.length === 0) {
        setPayslips([])
        return;
      }
      const transformedPayslips = payslipsData.map((payslip) => {
        const employee = payslip.employee || {};
        const bankInfo = employee.bank_info || {};

        return {
          id: payslip.id,
          month: getMonthName(payslip.month), // Convert month number to name
          monthNumber: payslip.month, // Keep original number for filtering
          year: payslip.year,
          gross_salary: payslip.gross_pay || 0,
          net_salary: payslip.net_pay || 0,
          total_deductions: payslip.total_deductions || 0,
          status: payslip.is_generated ? "generated" : null,
          generated_date: payslip.generated_date,
          days_in_month: getDaysInMonth(payslip.month, payslip.year),
          days_worked:
            payslip.total_working_days ||
            payslip.days_worked ||
            getDaysInMonth(payslip.month, payslip.year),
          total_calendar_days:
            payslip.total_calendar_days ||
            getDaysInMonth(payslip.month, payslip.year),
          total_leaves: getTotalLeaves(payslip),
          employee_details: {
            name: employee.name || user.name,
            employee_id: employee.employee_id || user.employee_id,
            designation: employee.designation || "N/A",
            department: employee.department || "N/A",
            date_of_joining: employee.date_of_joining,
            pan: bankInfo.pan || employee.pan || "N/A",
            bank_account: employee.bank_account || "N/A",
            ifsc: employee.ifsc || "N/A",
          },
          total_unpaid_leaves: payslip.total_unpaid_leaves || 0,
          earnings: payslip.earnings || {},
          // Full-month (pre-LOP) figures, used to show Loss of Pay as a deduction.
          monthly_earnings: payslip.monthly_earnings || [],
          monthly_gross_pay: payslip.monthly_gross_pay || 0,
          deductions: payslip.deductions || {},
          deductions_list: payslip.deductions_list || {},
          loan_deductions: payslip.loan_deductions || 0,
          reimbursements: payslip.reimbursements || [],
        };
      });

      // Sort payslips by year and month descending (latest first)
      transformedPayslips.sort((a, b) => {
        if (a.year !== b.year) {
          return b.year - a.year; // Sort by year descending
        }
        return b.monthNumber - a.monthNumber; // Sort by month descending
      });

      setPayslips(transformedPayslips);
      setPagination(response.pagination);
    } catch (err) {
      if (!isAuthExpiryError(err)) {
        toast.error("Failed to load payslips");
      }
    } finally {
      setInitialLoading(false);
      setListLoading(false);
    }
  };

  const generatePayslipPDF = async (payslip) => {
    const breakdown = getPayslipBreakdown(payslip);

    const formatCurrency = (amount) => {
      return `₹${Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    const formatDate = (date) => {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    // Get employee details
    const employeeDetails = payslip.employee_details || {};

    // Create a temporary div with the exact same design as admin portal
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.width = "800px";
    tempDiv.style.padding = "20px";
    tempDiv.style.backgroundColor = "white";
    tempDiv.style.fontFamily = "Arial, sans-serif";

    console.log("payslip", payslip);

    tempDiv.innerHTML = `
      <div style="padding: 20px; background: white;">
        <!-- Company Header -->
        <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px;">
          <div style="display: flex; justify-content: space-between; align-items: start;">
            <div style="display: flex; align-items: start;">
              ${
                companySettings?.logo_url
                  ? `<img src="${companySettings.logo_url}" alt="Company Logo" style="width: 60px; height: 60px; object-fit: contain; margin-right: 15px;" />`
                  : ""
              }
              <div>
                <h1 style="font-size: 24px; font-weight: bold; color: #10b981; margin: 0 0 5px 0;">${
                  companySettings?.company_name || "Element Tree"
                }</h1>
            <p
              style="
                font-size: 12px;
                color: #6b7280;
                max-width: 260px;
                line-height: 1.4;
                word-wrap: break-word;
                white-space: normal;
                display: block;
              "
            >                ${
              companySettings?.address ||
              "D-601, Raikar Chambers, off K.D. Marg, opp Neelkanth Gardens, Govandi (East), Mumbai - 400088, India"
            }</p>
                <p style="font-size: 12px; color: #6b7280; margin: 0;">Phone: ${
                  companySettings?.phone || "+91 22 1234 5678"
                } | Email: ${
      companySettings?.email || "hr@elementtree.co.in"
    }</p>
              </div>
            </div>
            <div style="text-align: right;">
              <h2 style="font-size: 20px; font-weight: 600; margin: 0;">SALARY SLIP</h2>
              <p style="font-size: 12px; color: #6b7280; margin: 5px 0 0 0;">For the month of ${
                payslip.month
              } ${payslip.year}</p>
            </div>
          </div>
        </div>

        <!-- Employee Details -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
          <div>
            <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px;">Employee Details</h3>
            <table style="width: 100%; font-size: 12px;">
              <tr><td style="padding: 2px 0; color: #6b7280;">Name:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.name || user.name
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Employee ID:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.employee_id
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Designation:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.designation || "N/A"
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Department:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.department || "N/A"
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Date of Joining:</td><td style="padding: 2px 0; font-weight: 500;">${formatDate(
                employeeDetails.date_of_joining
              )}</td></tr>
            </table>
          </div>
          <div>
            <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px;">Bank Details</h3>
            <table style="width: 100%; font-size: 12px;">
              <tr><td style="padding: 2px 0; color: #6b7280;">PAN:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.pan || "N/A"
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Bank Account:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.bank_account &&
                employeeDetails.bank_account !== "N/A"
                  ? "***" + String(employeeDetails.bank_account).slice(-4)
                  : "N/A"
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">IFSC Code:</td><td style="padding: 2px 0; font-weight: 500;">${
                employeeDetails.ifsc || "N/A"
              }</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Days Worked:</td><td style="padding: 2px 0; font-weight: 500;">${
                payslip.days_worked || 30
              } of ${payslip.total_calendar_days || 30}</td></tr>
              <tr><td style="padding: 2px 0; color: #6b7280;">Total Unpaid Leaves:</td><td style="padding: 2px 0; font-weight: 500;">${
                payslip.total_leaves || 0
              }</td></tr>
            </table>
          </div>
        </div>

          <!-- Salary Breakdown -->
       <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px;">
         <!-- Earnings -->
         <div style="background: #ecfdf5; padding: 15px; border-left: 4px solid #10b981; border-radius: 8px; min-height: 200px; display: flex; flex-direction: column;">
           <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #065f46;">Earnings</h3>
           <div style="flex: 1; overflow: visible;">
             <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
               ${breakdown.earnings
                 .map(
                   (item) => `
                 <tr>
                   <td style="padding: 4px 0; white-space: nowrap;">${
                     item.name
                   }:</td>
                   <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
                     item.amount
                   )}</td>
                 </tr>`
                 )
                 .join("")}
               <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
                 <td style="padding: 10px 0 0 0; font-weight: 700; color: #065f46;">Gross Earnings:</td>
                 <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #065f46;">
                   ${formatCurrency(breakdown.grossPay)}
                 </td>
               </tr>
             </table>
           </div>
         </div>
       
         <!-- Reimbursements -->
         ${
           payslip.reimbursements && payslip.reimbursements.length > 0
             ? `<div style="background: #eff6ff; padding: 15px; border-left: 4px solid #3b82f6; border-radius: 8px; min-height: 120px; display: flex; flex-direction: column;">
           <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #1e40af;">Reimbursements</h3>
           <div style="flex: 1; overflow: visible;">
             <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
               ${payslip.reimbursements
                 .map(
                   (item) => `
                 <tr>
                   <td style="padding: 4px 0; white-space: nowrap; text-transform: capitalize;">${item.claim_type || "Reimbursement"}:</td>
                   <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(item.amount)}</td>
                 </tr>`
                 )
                 .join("")}
               <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
                 <td style="padding: 10px 0 0 0; font-weight: 700; color: #1e40af;">Total Reimbursements:</td>
                 <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #1e40af;">
                   ${formatCurrency(payslip.reimbursements.reduce((s, r) => s + Number(r.amount || 0), 0))}
                 </td>
               </tr>
             </table>
           </div>
         </div>`
             : ""
         }

         <!-- Deductions -->
         <div style="background: #fef2f2; padding: 15px; border-left: 4px solid #dc2626; border-radius: 8px; min-height: 200px; display: flex; flex-direction: column;">
           <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #991b1b;">Deductions</h3>
           <div style="flex: 1; overflow: visible;">
             <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
               ${
                 breakdown.lopAmount > 0
                   ? `<tr>
                     <td style="padding: 4px 0; white-space: nowrap;">${
                       breakdown.lopLabel
                     }:</td>
                     <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
                       breakdown.lopAmount
                     )}</td>
                   </tr>`
                   : ""
               }
               ${payslip.deductions_list
                 .map(
                   (item) => `
                 <tr>
                   <td style="padding: 4px 0; white-space: nowrap;">${
                     item.name
                   }:</td>
                   <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
                     item.amount
                   )}</td>
                 </tr>`
                 )
                 .join("")}
                ${Object.entries(payslip.deductions || {})
                  .map(([key, value]) => {
                    if (!value || Number(value) === 0) return "";
                    if (isDuplicateLoanKey(key)) return "";
                    return `
                      <tr>
                        <td style="padding: 4px 0; white-space: nowrap;">
                          ${formatLabel(key)}:
                        </td>
                        <td style="padding: 4px 0; text-align: right; width: 100%;">
                          ${formatCurrency(value)}
                        </td>
                      </tr>`;
                  })
                  .join("")}
               ${
                 payslip.loan_deductions > 0
                   ? `<tr>
                     <td style="padding: 4px 0; white-space: nowrap;">Loan EMI:</td>
                     <td style="padding: 4px 0; text-align: right;">${formatCurrency(
                       payslip.loan_deductions
                     )}</td>
                   </tr>`
                   : ""
               }
               <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
                 <td style="padding: 10px 0 0 0; font-weight: 700; color: #991b1b;">Total Deductions:</td>
                 <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #991b1b;">
                   ${formatCurrency(breakdown.totalDeductions)}
                 </td>
               </tr>
             </table>
           </div>
         </div>
       </div>
       

        <!-- Net Salary -->
        <div style="border-top: 2px solid #e5e7eb; margin-top: 20px; padding-top: 20px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 16px; font-weight: 600;">Net Salary:</span>
            <span style="font-size: 24px; font-weight: bold; color: #10b981;">${formatCurrency(
              payslip.net_salary
            )}</span>
          </div>
          <p style="font-size: 12px; color: #6b7280; margin-top: 10px;">Generated on: ${formatDate(
            payslip.generated_date
          )}</p>
        </div>
      </div>
    `;

    document.body.appendChild(tempDiv);

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
    });

    document.body.removeChild(tempDiv);

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const fileName = `Payslip_${payslip.month}_${payslip.year}.pdf`;

    if (!Capacitor.isNativePlatform()) {
      // Web/Desktop
      pdf.save(fileName);
      return null;
    }

    // Native (Android/iOS): stage the file in the app's own cache and hand back
    // its URI for the share sheet. Directory.Documents is shared storage, where
    // Android 11+ refuses to overwrite a file this app doesn't own in MediaStore
    // (e.g. one left by an earlier install), which broke re-downloading a
    // payslip. The cache directory is always writable, and the FileProvider
    // already exposes it (file_paths.xml). The user keeps a copy through the
    // share sheet ("Save to Files", Drive, ...).
    const pdfBase64 = pdf.output("datauristring").split(",")[1];

    await Filesystem.writeFile({
      path: fileName,
      data: pdfBase64,
      directory: Directory.Cache,
    });

    const uriResult = await Filesystem.getUri({
      directory: Directory.Cache,
      path: fileName,
    });

    return uriResult.uri;
  };

  const handleDownload = async (payslip) => {
    // Stage 1: build (and on web, save) the PDF. Failures here are real failures.
    let fileUri;
    try {
      fileUri = await generatePayslipPDF(payslip);
    } catch (e) {
      console.error(e);
      toast.error("Failed to download payslip");
      return;
    }

    // Web: pdf.save() already downloaded the file.
    if (!fileUri) {
      toast.success("Payslip downloaded");
      return;
    }

    // Stage 2 (native): the file exists, so an error from the share sheet must
    // not be reported as a failed download. Share.share() rejects with
    // "Share canceled" whenever the sheet closes without the app being stopped,
    // which on Android 16 includes dismissing it or picking a target that
    // opens as an overlay.
    try {
      await Share.share({
        title: "Payslip",
        text: "Your payslip PDF is ready.",
        url: fileUri,
        dialogTitle: "Open payslip",
      });
      toast.success("Payslip downloaded");
    } catch (e) {
      if (/cancel/i.test(e?.message ?? "")) return;
      console.error(e);
      toast.error("Payslip generated, but the share sheet could not be opened");
    }
  };

  const handlePreview = (payslip) => {
    setPreviewPayslip(payslip);
  };

  const availableYears = [...new Set(payslips.map((p) => p.year))].sort(
    (a, b) => b - a
  );

  if (initialLoading) {
    return <EmployeePayslipsSkeleton employeeId={employeeId} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!employeeId.employeeId && (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              My Payslips
            </h1>
            <p className="text-gray-500">
              View and download your salary statements
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div
        className={`grid md:grid-cols-3 gap-6 ${
          employeeId.employeeId && "mt-10"
        }`}
      >
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Payslips
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {summary?.totalPayslips ?? 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Latest Salary
                </p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{(summary?.latestSalary || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  YTD Earnings
                </p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ₹{(summary?.ytdEarnings || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Salary Breakdown */}
      {employeeData?.salary_structure && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <IndianRupee className="h-5 w-5 text-primary" />
              <span>Current Salary Structure</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-500/30">
                <h3 className="font-semibold mb-4 text-emerald-700 dark:text-emerald-400 flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>Earnings</span>
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Basic Salary</span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.basic_salary || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      House Rent Allowance
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.house_rent_allowance ||
                        employeeData.salary_structure.hra ||
                        0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Medical Allowance
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.medical_allowance || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Leave Travel Allowance
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.leave_travel_allowance ||
                        employeeData.salary_structure.travel_allowance ||
                        0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Bonus</span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.conveyance_allowance ||
                        employeeData.salary_structure.food_allowance ||
                        0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Performance Incentive
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.performance_incentive ||
                        employeeData.salary_structure.internet_allowance ||
                        0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Other Benefits
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.other_benefits ||
                        employeeData.salary_structure.special_allowance ||
                        0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-primary">
                        Gross Salary
                      </span>
                      <span className="font-bold text-primary">
                        ₹
                        {(
                          (employeeData.salary_structure.basic_salary || 0) +
                          (employeeData.salary_structure.house_rent_allowance ||
                            employeeData.salary_structure.hra ||
                            0) +
                          (employeeData.salary_structure.medical_allowance ||
                            0) +
                          (employeeData.salary_structure
                            .leave_travel_allowance ||
                            employeeData.salary_structure.travel_allowance ||
                            0) +
                          (employeeData.salary_structure.conveyance_allowance ||
                            employeeData.salary_structure.food_allowance ||
                            0) +
                          (employeeData.salary_structure
                            .performance_incentive ||
                            employeeData.salary_structure.internet_allowance ||
                            0) +
                          (employeeData.salary_structure.other_benefits ||
                            employeeData.salary_structure.special_allowance ||
                            0)
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-500/30">
                <h3 className="font-semibold mb-4 text-red-700 dark:text-red-400 flex items-center space-x-2">
                  <IndianRupee className="h-4 w-4" />
                  <span>Deductions</span>
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">PF (Employee)</span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.pf_employee || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      ESI (Employee)
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.esi_employee || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Professional Tax
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.professional_tax || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">TDS</span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.tds || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">
                      Loan Deductions
                    </span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.loan_deductions || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Others</span>
                    <span className="font-semibold">
                      ₹
                      {(
                        employeeData.salary_structure.others || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-destructive">
                        Total Deductions
                      </span>
                      <span className="font-semibold text-destructive">
                        ₹
                        {(
                          (employeeData.salary_structure.pf_employee || 0) +
                          (employeeData.salary_structure.esi_employee || 0) +
                          (employeeData.salary_structure.professional_tax ||
                            0) +
                          (employeeData.salary_structure.tds || 0) +
                          (employeeData.salary_structure.loan_deductions || 0) +
                          (employeeData.salary_structure.others || 0)
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Salary - Full Width Box */}
            <div className="mt-6">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 p-6 rounded-lg border-2 border-primary/20">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                      <IndianRupee className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <div>
                      <span className="text-xl font-bold text-primary">
                        Net Salary
                      </span>
                      <p className="text-sm text-muted-foreground">
                        Monthly Take-home Amount
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-primary">
                      ₹
                      {(
                        (employeeData.salary_structure.basic_salary || 0) +
                        (employeeData.salary_structure.house_rent_allowance ||
                          employeeData.salary_structure.hra ||
                          0) +
                        (employeeData.salary_structure.medical_allowance || 0) +
                        (employeeData.salary_structure.leave_travel_allowance ||
                          employeeData.salary_structure.travel_allowance ||
                          0) +
                        (employeeData.salary_structure.conveyance_allowance ||
                          employeeData.salary_structure.food_allowance ||
                          0) +
                        (employeeData.salary_structure.performance_incentive ||
                          employeeData.salary_structure.internet_allowance ||
                          0) +
                        (employeeData.salary_structure.other_benefits ||
                          employeeData.salary_structure.special_allowance ||
                          0) -
                        ((employeeData.salary_structure.pf_employee || 0) +
                          (employeeData.salary_structure.esi_employee || 0) +
                          (employeeData.salary_structure.professional_tax ||
                            0) +
                          (employeeData.salary_structure.tds || 0) +
                          (employeeData.salary_structure.loan_deductions || 0) +
                          (employeeData.salary_structure.others || 0))
                      ).toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Gross: ₹
                      {(
                        (employeeData.salary_structure.basic_salary || 0) +
                        (employeeData.salary_structure.house_rent_allowance ||
                          employeeData.salary_structure.hra ||
                          0) +
                        (employeeData.salary_structure.medical_allowance || 0) +
                        (employeeData.salary_structure.leave_travel_allowance ||
                          employeeData.salary_structure.travel_allowance ||
                          0) +
                        (employeeData.salary_structure.conveyance_allowance ||
                          employeeData.salary_structure.food_allowance ||
                          0) +
                        (employeeData.salary_structure.performance_incentive ||
                          employeeData.salary_structure.internet_allowance ||
                          0) +
                        (employeeData.salary_structure.other_benefits ||
                          employeeData.salary_structure.special_allowance ||
                          0)
                      ).toLocaleString()}
                      - Deductions: ₹
                      {(
                        (employeeData.salary_structure.pf_employee || 0) +
                        (employeeData.salary_structure.esi_employee || 0) +
                        (employeeData.salary_structure.professional_tax || 0) +
                        (employeeData.salary_structure.tds || 0) +
                        (employeeData.salary_structure.loan_deductions || 0) +
                        (employeeData.salary_structure.others || 0)
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by month or year..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="termination">Termination</SelectItem>
                <SelectItem value="one_time">One Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payslips List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {listLoading && (
          <div className="col-span-full text-center text-sm text-muted-foreground">
            Loading payslips…
          </div>
        )}

        {payslips.map((payslip) => (
          <Card
            key={payslip.id}
            className="hover:shadow-lg transition-shadow cursor-pointer hover:bg-muted/50"
            onClick={() => {
              handlePreview(payslip);
              setOpen(true);
            }}
          >
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>
                  {payslip.month} {payslip.year}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Gross Salary
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    ₹{payslip.gross_salary?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Deductions
                  </p>
                  <p className="font-semibold text-red-600 dark:text-red-400">
                    ₹{payslip.total_deductions?.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-500/30">
                <p className="text-sm text-green-600 dark:text-green-400">
                  Net Salary
                </p>
                <p className="text-xl font-bold text-green-700 dark:text-green-400">
                  ₹{payslip.net_salary?.toLocaleString()}
                </p>
              </div>

              <div
                className="flex space-x-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        handlePreview(payslip);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </DialogTrigger>

                  <DialogContent
                    className="
      w-[95vw]
      max-w-4xl
      max-h-[92vh]
      overflow-y-auto
      p-3 sm:p-6
    "
                  >
                    <DialogClose asChild>
                      <button
                        className="
      absolute right-4 top-4
      rounded-sm opacity-70
      transition-opacity hover:opacity-100
      focus:outline-none
    "
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </DialogClose>
                    <DialogHeader className="pb-3">
                      <DialogTitle className="text-base sm:text-lg">
                        Payslip Preview –{" "}
                        {previewPayslip?.employee_details?.name || user.name}
                      </DialogTitle>
                    </DialogHeader>

                    {previewPayslip && (
                      <div
                        className="
          bg-white dark:bg-gray-900
          p-4 sm:p-8
          rounded-lg
          border dark:border-gray-700
          shadow-sm
        "
                      >
                        {/* ================= COMPANY HEADER ================= */}
                        <div className="border-b dark:border-gray-700 pb-5 mb-6">
                          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
                            <div className="flex items-start gap-3">
                              {companySettings?.logo_url && (
                                <img
                                  src={companySettings.logo_url}
                                  alt="Company Logo"
                                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                                  onError={(e) =>
                                    (e.currentTarget.style.display = "none")
                                  }
                                />
                              )}

                              <div className="min-w-0">
                                <h1 className="text-xl sm:text-2xl font-bold text-primary">
                                  {companySettings?.company_name ||
                                    "Element Tree"}
                                </h1>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words max-w-md">
                                  {companySettings?.address}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                  Phone: {companySettings?.phone} | Email:{" "}
                                  {companySettings?.email}
                                </p>
                              </div>
                            </div>

                            <div className="text-left sm:text-right">
                              <h2 className="text-lg sm:text-xl font-semibold">
                                SALARY SLIP
                              </h2>
                              <p className="text-xs sm:text-sm text-gray-500">
                                {previewPayslip.month} {previewPayslip.year}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* ================= EMPLOYEE & BANK DETAILS ================= */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
                          {/* Employee Details */}
                          <div>
                            <h3 className="font-semibold mb-3">
                              Employee Details
                            </h3>
                            <div className="space-y-2 text-sm">
                              {[
                                ["Name", previewPayslip.employee_details?.name],
                                [
                                  "Employee ID",
                                  previewPayslip.employee_details?.employee_id,
                                ],
                                [
                                  "Designation",
                                  previewPayslip.employee_details?.designation,
                                ],
                                [
                                  "Department",
                                  previewPayslip.employee_details?.department,
                                ],
                              ].map(([label, value]) => (
                                <div
                                  key={label}
                                  className="flex justify-between gap-3"
                                >
                                  <span className="text-gray-600 shrink-0">
                                    {label}:
                                  </span>
                                  <span className="font-medium text-right break-words">
                                    {value || "N/A"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Bank Details */}
                          <div>
                            <h3 className="font-semibold mb-3">Bank Details</h3>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-600">PAN:</span>
                                <span className="font-medium">
                                  {previewPayslip.employee_details?.pan ||
                                    "N/A"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-600">
                                  Bank Account:
                                </span>
                                <span className="font-medium tabular-nums">
                                  {previewPayslip.employee_details?.bank_account
                                    ? "***" +
                                      String(
                                        previewPayslip.employee_details
                                          .bank_account,
                                      ).slice(-4)
                                    : "N/A"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-600">IFSC:</span>
                                <span className="font-medium">
                                  {previewPayslip.employee_details?.ifsc ||
                                    "N/A"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-600">
                                  Days Worked:
                                </span>
                                <span className="font-medium">
                                  {previewPayslip.days_worked || 30} of{" "}
                                  {previewPayslip.total_calendar_days || 30}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-600">
                                  Total Unpaid Leaves:
                                </span>
                                <span className="font-medium">
                                  {previewPayslip.total_leaves || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ================= EARNINGS & DEDUCTIONS ================= */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          {/* Earnings */}
                          <div className="bg-emerald-50 dark:bg-primary/20 p-3 sm:p-4 rounded-lg border-l-4 border-primary">
                            <h3 className="font-semibold mb-3 text-primary">
                              Earnings
                            </h3>

                            <div className="space-y-2 text-sm">
                              {previewBreakdown.earnings?.map((item, idx) => (
                                <div key={idx} className="flex justify-between">
                                  <span>{item.name}</span>
                                  <span className="tabular-nums whitespace-nowrap">
                                    {formatCurrency(item.amount)}
                                  </span>
                                </div>
                              ))}

                              <div className="flex justify-between border-t pt-2 font-semibold">
                                <span>Gross Earnings</span>
                                <span className="tabular-nums">
                                  {formatCurrency(previewBreakdown.grossPay)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Reimbursements */}
                          {previewPayslip.reimbursements?.length > 0 && (
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4 rounded-lg border-l-4 border-blue-500">
                              <h3 className="font-semibold mb-3 text-blue-700 dark:text-blue-400">
                                Reimbursements
                              </h3>

                              <div className="space-y-2 text-sm">
                                {previewPayslip.reimbursements.map(
                                  (item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex justify-between"
                                    >
                                      <span className="capitalize">
                                        {item.claim_type || "Reimbursement"}
                                      </span>
                                      <span className="tabular-nums whitespace-nowrap">
                                        {formatCurrency(item.amount)}
                                      </span>
                                    </div>
                                  ),
                                )}

                                <div className="flex justify-between border-t pt-2 font-semibold">
                                  <span>Total Reimbursements</span>
                                  <span className="tabular-nums">
                                    {formatCurrency(
                                      previewPayslip.reimbursements.reduce(
                                        (s, r) => s + Number(r.amount || 0),
                                        0,
                                      ),
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Deductions */}
                          <div className="bg-red-50 dark:bg-red-900/20 p-3 sm:p-4 rounded-lg border-l-4 border-red-500">
                            <h3 className="font-semibold mb-3 text-red-600">
                              Deductions
                            </h3>

                            <div className="space-y-2 text-sm">
                              {/* Loss of Pay (unpaid leave) */}
                              {previewBreakdown.lopAmount > 0 && (
                                <div className="flex justify-between">
                                  <span>{previewBreakdown.lopLabel}</span>
                                  <span className="tabular-nums whitespace-nowrap">
                                    {formatCurrency(
                                      previewBreakdown.lopAmount,
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Manual deductions */}
                              {previewPayslip.deductions_list?.map(
                                (item, idx) => (
                                  <div
                                    key={idx}
                                    className="flex justify-between"
                                  >
                                    <span>{item.name}</span>
                                    <span className="tabular-nums whitespace-nowrap">
                                      {formatCurrency(item.amount)}
                                    </span>
                                  </div>
                                ),
                              )}

                              {/* Statutory deductions (EPF, ESI, PT, LWF, TDS) */}
                              {Object.entries(
                                previewPayslip.deductions || {},
                              ).map(([key, value]) => {
                                if (isDuplicateLoanKey(key)) return null; // handled separately
                                if (!value || Number(value) === 0) return null;

                                return (
                                  <div
                                    key={key}
                                    className="flex justify-between"
                                  >
                                    <span>{formatLabel(key)}</span>
                                    <span className="tabular-nums whitespace-nowrap">
                                      {formatCurrency(value)}
                                    </span>
                                  </div>
                                );
                              })}

                              {/* Loan deductions */}
                              {previewPayslip.loan_deductions > 0 && (
                                <div className="flex justify-between">
                                  <span>Loan EMI</span>
                                  <span className="tabular-nums whitespace-nowrap">
                                    {formatCurrency(
                                      previewPayslip.loan_deductions,
                                    )}
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between border-t pt-2 font-semibold">
                                <span>Total Deductions</span>
                                <span className="tabular-nums">
                                  {formatCurrency(
                                    previewBreakdown.totalDeductions,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ================= NET SALARY ================= */}
                        <div className="border-t mt-6 pt-5">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                            <span className="text-lg font-semibold">
                              Net Salary
                            </span>
                            <span className="text-xl sm:text-2xl font-bold text-primary tabular-nums">
                              ₹{previewPayslip.net_salary?.toLocaleString()}
                            </span>
                          </div>

                          <p className="text-xs text-gray-500 mt-2">
                            Generated on{" "}
                            {new Date(
                              previewPayslip.generated_date,
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => handleDownload(payslip)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>

          <span className="px-4 py-2 text-sm">
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <Button
            variant="outline"
            disabled={page === pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {payslips.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No payslips found</h3>
            <p className="text-gray-500">
              {searchTerm || yearFilter !== "all" || typeFilter !== "all"
                ? "Try adjusting your search filters"
                : "Your payslips will appear here once they are generated"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeePayslips;


function EmployeePayslipsSkeleton({ employeeId }) {
  return (
    <div className="space-y-6">
      {!employeeId.employeeId && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-72" />
        </div>
      )}

      <div
        className={`grid gap-6 md:grid-cols-3 ${employeeId.employeeId && "mt-10"}`}
      >
        {[...Array(3)].map((_, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-7 w-16" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-[160px]" />
          </div>
          {[...Array(3)].map((_, index) => (
            <div key={index} className="rounded-xl border p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="space-y-2 text-right">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-9 w-28" />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}